import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import WeightLineChart from '../WeightLineChart.js';
import DateInput from '../../shared/inputs/DateInput.js';
import ErrorMessage from '../../shared/ErrorMessage.js';
import './WeightLots.css';

const localDateStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatSessionDate = (dateString) => {
  if (!dateString) return '—';
  const s = dateString.length === 10 ? dateString + 'T12:00:00' : dateString;
  const d = new Date(s);
  if (isNaN(d)) return dateString;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
};

const WeightLotSessions = () => {
  const { lotId }      = useParams();
  const { t }          = useTranslation();
  const today          = localDateStr();

  const [sessions,     setSessions]     = useState([]);
  const [weight,       setWeight]       = useState('');
  const [date,         setDate]         = useState(today);
  const [errorMessage, setErrorMessage] = useState('');

  const load = useCallback(() => {
    Fetch(`api/data/weight.lots.weights?search_fields=lot_id&search_values=${lotId}`, 'get', null,
      (data) => setSessions((data || []).sort((a, b) => a.date.localeCompare(b.date)))
    );
  }, [lotId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!weight || parseFloat(weight) <= 0) return;
    Fetch('api/data/weight.lots.weights', 'post',
      JSON.stringify({ lot_id: Number(lotId), date, weight: parseFloat(weight) }),
      () => { setWeight(''); setDate(today); load(); },
      () => setErrorMessage(t('error_saving'))
    );
  };

  const chartPoints = sessions.map(s => ({ date: s.date, weight: s.weight }));

  return (
    <div className="wld-page">
      <div className="wld-subpage-title">{t('weightSessions')}</div>

      {chartPoints.length > 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, marginBottom: 4 }}>
          {t('lot_session_weight_chart_label')}
        </div>
      )}
      <WeightLineChart points={chartPoints} />

      <div style={{ marginTop: 24 }}>
        {sessions.length === 0 ? (
          <div className="wl-empty-note">{t('noWeighIns')}</div>
        ) : (
          <div className="wl-sessions-list">
            {[...sessions].reverse().map((s, i) => (
              <div key={i} className="wl-session-row">
                <span className="wl-session-date">{formatSessionDate(s.date)}</span>
                <span className="wl-session-weight">{s.weight} kg</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <form className="wl-inline-form" onSubmit={handleSubmit}>
        <input type="number" value={weight} onChange={e => setWeight(e.target.value)}
          placeholder={t('unit_kg')} min="1" max="9999" step="0.1" required />
        <DateInput value={date} onChange={(v) => setDate(v)} required />
        <button type="submit" className="wl-inline-btn">+</button>
      </form>
      <ErrorMessage message={errorMessage} />
    </div>
  );
};

export default WeightLotSessions;
